import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const TimeBasedInsightsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/financial-analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading time-based insights...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Time-Based Insights</h1>
          <p className="text-purple-200 text-sm">Subscription tenure and conversion timing analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Time-Based Insights</h1>
              <p className="text-purple-200 text-sm">Subscription tenure and conversion timing analytics</p>
            </div>
          </div>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-gray-600">
        Last updated: {formatDateTime(analytics.last_updated)}
      </div>

      <div className="space-y-8">
        {/* Chart 17: Subscription Tenure Distribution */}
        {analytics && analytics.tenure_distribution ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Tenure Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={Object.entries(analytics.tenure_distribution).map(([range, count]) => ({
                  range,
                  count
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#6366f1" name="Customers" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> How long your current customers have been subscribed, grouped into time ranges (0-3 months, 3-6 months, 6-12 months, 12+ months).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Many customers in 12+ months shows strong retention. Heavy concentration in 0-3 months with few in 12+ suggests retention problems - customers aren't sticking around. Ideal distribution has customers spread across all ranges with a solid base in 12+. Focus retention efforts on moving customers from early stages to long-term.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Tenure Distribution</h3>
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No tenure data available. Create PayPal subscriptions to see data.
            </div>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> How long your current customers have been subscribed, grouped into time ranges (0-3 months, 3-6 months, 6-12 months, 12+ months).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Many customers in 12+ months shows strong retention. Heavy concentration in 0-3 months with few in 12+ suggests retention problems - customers aren't sticking around. Ideal distribution has customers spread across all ranges with a solid base in 12+. Focus retention efforts on moving customers from early stages to long-term.
              </p>
            </div>
          </div>
        )}

        {/* Chart 18: Time to Convert from Trial */}
        {analytics && analytics.conversion_time_distribution ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Time to Convert from Trial</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={Object.entries(analytics.conversion_time_distribution).map(([range, count]) => ({
                  range,
                  count
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#10b981" name="Conversions" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> How many days into the trial period customers typically convert to paid subscriptions (0-7, 8-14, 15-21, 22-30 days).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Most conversions in 0-7 days? Your value proposition is immediately clear - great! Conversions clustered at 22-30 days? Customers need the full trial to decide - consider stronger early engagement or more compelling features. Few early conversions? Add "aha moments" in the first week to drive earlier decisions and reduce trial abuse.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Time to Convert from Trial</h3>
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No conversion data available. Trials need to convert first.
            </div>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> How many days into the trial period customers typically convert to paid subscriptions (0-7, 8-14, 15-21, 22-30 days).
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Most conversions in 0-7 days? Your value proposition is immediately clear - great! Conversions clustered at 22-30 days? Customers need the full trial to decide - consider stronger early engagement or more compelling features. Few early conversions? Add "aha moments" in the first week to drive earlier decisions and reduce trial abuse.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeBasedInsightsPage;
